import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, ChevronsUpDown, ShieldCheck, Sparkles, UserCircle } from 'lucide-react';
import { Link, Navigate, Outlet, useLocation, useNavigate } from 'react-router';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LoadingState } from '@/components/shared/states';
import { api } from '@/lib/api';
import { meKey, tenantGate, useMe, useSession } from '@/lib/session';
import { NAV_GROUPS } from './nav';
import { AccountLink, AppShell, SidebarNav, UserMenuShell, useAppTheme } from './shell';
import { ImpersonationBanner } from './impersonation-banner';
import { SubscriptionBanner } from './subscription-banner';

function TenantNav({ onNavigate }: { onNavigate?: () => void }) {
  const { can } = useSession();
  const groups = NAV_GROUPS.map((g) => ({
    label: g.label,
    items: g.items.filter((i) => !i.anyOf || i.anyOf.some(can)),
  }));
  return <SidebarNav groups={groups} rootTo="/app" onNavigate={onNavigate} />;
}

function TenantAccount({ onNavigate }: { onNavigate?: () => void }) {
  const { me } = useSession();
  if (!me) return null;
  return <AccountLink to="/app/conta" email={me.user.email} onNavigate={onNavigate} />;
}

function OrgSwitcher() {
  const { me, org } = useSession();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const switchOrg = useMutation({
    mutationFn: (organizationId: string) => api.post('/auth/switch-organization', { organizationId }),
    onSuccess: async () => {
      qc.clear();
      await qc.invalidateQueries({ queryKey: meKey });
      navigate('/app');
    },
  });
  if (!me || !org) return null;
  const many = me.memberships.length > 1;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="min-w-0 max-w-[38vw] gap-2 px-2 sm:max-w-[60vw] sm:px-3"
          aria-label={`Barbearia ativa: ${org.organizationName}. Trocar de barbearia`}
        >
          <span className="truncate font-display text-base font-semibold">{org.organizationName}</span>
          {many && <ChevronsUpDown className="size-4 shrink-0 text-muted-foreground" aria-hidden />}
        </Button>
      </DropdownMenuTrigger>
      {many && (
        <DropdownMenuContent align="start" className="min-w-64">
          <DropdownMenuLabel>Trocar de barbearia</DropdownMenuLabel>
          {me.memberships.map((m) => (
            <DropdownMenuItem
              key={m.organizationId}
              onSelect={() => m.organizationId !== org.organizationId && switchOrg.mutate(m.organizationId)}
            >
              <span className="min-w-0 flex-1">
                <span className="block truncate font-medium">{m.organizationName}</span>
                <span className="block truncate text-xs text-muted-foreground">{m.roleName}</span>
              </span>
              {m.organizationId === org.organizationId && (
                <Check className="size-4 text-primary" aria-label="Ativa" />
              )}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      )}
    </DropdownMenu>
  );
}

function UserMenu() {
  const { me, org } = useSession();
  const navigate = useNavigate();
  if (!me) return null;
  return (
    <UserMenuShell name={me.user.name} email={me.user.email} roleLine={org?.roleName}>
      <DropdownMenuItem onSelect={() => navigate('/app/conta')}>
        <UserCircle className="size-4" aria-hidden /> Minha conta
      </DropdownMenuItem>
      {me.platformRole && (
        <DropdownMenuItem onSelect={() => navigate('/plataforma')}>
          <ShieldCheck className="size-4" aria-hidden /> Plataforma
        </DropdownMenuItem>
      )}
    </UserMenuShell>
  );
}

function AssistantButton() {
  const { can } = useSession();
  if (!can('ai.use')) return null;
  return (
    <Button asChild variant="ghost" size="icon" aria-label="Assistente">
      <Link to="/app/assistente">
        <Sparkles className="size-5" aria-hidden />
      </Link>
    </Button>
  );
}

export function AppLayout() {
  useAppTheme();
  const { data: me, isLoading } = useMe();
  const location = useLocation();

  if (isLoading) return <LoadingState />;
  if (!me)
    return (
      <Navigate to={`/entrar?next=${encodeURIComponent(location.pathname + location.search)}`} replace />
    );
  // operador da plataforma nunca usa telas de tenant sem impersonar
  if (me.platformAdmin && !me.impersonation) return <Navigate to="/plataforma" replace />;
  if (!me.activeOrganizationId) return <Navigate to="/cadastrar?etapa=barbearia" replace />;
  // senha definida pelo operador: só libera "Minha conta" até o usuário criar a própria; depois, o aceite dos termos
  const gate = tenantGate(me, location.pathname);
  if (gate) return <Navigate to={gate} replace />;

  return (
    <AppShell
      brandTo="/app"
      renderNav={(close) => <TenantNav onNavigate={close} />}
      renderAccount={(close) => <TenantAccount onNavigate={close} />}
      banner={me.impersonation && <ImpersonationBanner info={me.impersonation} />}
      headerStart={<OrgSwitcher />}
      headerEnd={
        <>
          <AssistantButton />
          <UserMenu />
        </>
      }
      afterHeader={<SubscriptionBanner />}
    >
      <Outlet />
    </AppShell>
  );
}
